// Configuration types
export interface AppConfig {
  REACT_APP_COGNITO_REGION: string;
  REACT_APP_COGNITO_USER_POOL_ID: string;
  REACT_APP_COGNITO_USER_POOL_CLIENT_ID: string;
  REACT_APP_COGNITO_DOMAIN: string;
  REACT_APP_COGNITO_REDIRECT_SIGN_IN: string;
  REACT_APP_COGNITO_REDIRECT_SIGN_OUT: string;
  REACT_APP_API_URL: string;
  REACT_APP_USE_MOCK_API: string;
  REACT_APP_AUTH_PROVIDER: string;
  REACT_APP_USE_MOCK_AUTH: string;
  REACT_APP_AI_HUB_BASE_URL: string;
}

// Extend Window interface to include APP_CONFIG
declare global {
  interface Window {
    APP_CONFIG?: AppConfig;
  }
}

// Default configuration (fallback values)
const defaultConfig: AppConfig = {
  REACT_APP_COGNITO_REGION: 'eu-west-1',
  REACT_APP_COGNITO_USER_POOL_ID: '',
  REACT_APP_COGNITO_USER_POOL_CLIENT_ID: '',
  REACT_APP_COGNITO_DOMAIN: '',
  REACT_APP_COGNITO_REDIRECT_SIGN_IN: window.location.origin,
  REACT_APP_COGNITO_REDIRECT_SIGN_OUT: window.location.origin,
  REACT_APP_API_URL: 'https://localhost:5000/api/v1',
  REACT_APP_USE_MOCK_API: 'false',
  REACT_APP_AUTH_PROVIDER: 'amplify',
  REACT_APP_USE_MOCK_AUTH: 'false',
  REACT_APP_AI_HUB_BASE_URL: ''
};

// Global config object that will be populated
let appConfig: AppConfig = { ...defaultConfig };

/**
 * Load configuration from the /config.js endpoint
 */
export const loadConfig = async (): Promise<AppConfig> => {
  try {
    // Check if we're in development mode and should use environment variables
    if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_API_URL) {
      return {
        REACT_APP_COGNITO_REGION:
          process.env.REACT_APP_COGNITO_REGION || defaultConfig.REACT_APP_COGNITO_REGION,
        REACT_APP_COGNITO_USER_POOL_ID:
          process.env.REACT_APP_COGNITO_USER_POOL_ID ||
          defaultConfig.REACT_APP_COGNITO_USER_POOL_ID,
        REACT_APP_COGNITO_USER_POOL_CLIENT_ID:
          process.env.REACT_APP_COGNITO_USER_POOL_CLIENT_ID ||
          defaultConfig.REACT_APP_COGNITO_USER_POOL_CLIENT_ID,
        REACT_APP_COGNITO_DOMAIN:
          process.env.REACT_APP_COGNITO_DOMAIN || defaultConfig.REACT_APP_COGNITO_DOMAIN,
        REACT_APP_COGNITO_REDIRECT_SIGN_IN:
          process.env.REACT_APP_COGNITO_REDIRECT_SIGN_IN ||
          defaultConfig.REACT_APP_COGNITO_REDIRECT_SIGN_IN,
        REACT_APP_COGNITO_REDIRECT_SIGN_OUT:
          process.env.REACT_APP_COGNITO_REDIRECT_SIGN_OUT ||
          defaultConfig.REACT_APP_COGNITO_REDIRECT_SIGN_OUT,
        REACT_APP_API_URL: process.env.REACT_APP_API_URL || defaultConfig.REACT_APP_API_URL,
        REACT_APP_USE_MOCK_API:
          process.env.REACT_APP_USE_MOCK_API || defaultConfig.REACT_APP_USE_MOCK_API,
        REACT_APP_AUTH_PROVIDER:
          process.env.REACT_APP_AUTH_PROVIDER || defaultConfig.REACT_APP_AUTH_PROVIDER,
        REACT_APP_USE_MOCK_AUTH:
          process.env.REACT_APP_USE_MOCK_AUTH || defaultConfig.REACT_APP_USE_MOCK_AUTH,
        REACT_APP_AI_HUB_BASE_URL:
          process.env.REACT_APP_AI_HUB_BASE_URL || defaultConfig.REACT_APP_AI_HUB_BASE_URL,
      };
    }

    // In production, load from /config.js

    const response = await fetch('/config.js');
    if (!response.ok) {
      throw new Error(`Failed to load config: ${response.status} ${response.statusText}`);
    }

    const configText = await response.text();

    // Try to execute the config.js as JavaScript to get the window.APP_CONFIG
    // This is more reliable than regex parsing
    let loadedConfig: AppConfig | undefined;
    let script: HTMLScriptElement | undefined;

    // First, try to validate the JavaScript using eval in a safe context
    // This will catch syntax errors before we try to appendChild
    try {
      // Create a safe evaluation context
      const testWindow = { APP_CONFIG: undefined };
      const testCode = configText.replace(/window\.APP_CONFIG/, 'testWindow.APP_CONFIG');

      // Try to evaluate the code safely
      eval(`(function(testWindow) { ${testCode} })`)(testWindow); // eslint-disable-line security/detect-eval-with-expression

      // If eval succeeded, the JavaScript is valid, proceed with DOM approach
      try {
        // Clear any existing config to avoid pollution
        delete window.APP_CONFIG;

        script = document.createElement('script');
        script.textContent = configText;

        // This line can throw in JSDOM when invalid JavaScript is provided
        document.head.appendChild(script);

        // Access the global window.APP_CONFIG
        loadedConfig = window.APP_CONFIG;
      } catch (domError) {
        console.error('Failed to execute config via DOM:', domError);
        loadedConfig = undefined;
      } finally {
        // Clean up script element if it was created and added to DOM
        if (script && script.parentNode) {
          try {
            document.head.removeChild(script);
          } catch (cleanupError) {
            // Ignore cleanup errors
          }
        }
      }
    } catch (syntaxError) {
      console.error('Failed to parse config JavaScript:', syntaxError);
      // JavaScript is invalid, don't even try appendChild
      loadedConfig = undefined;
    }
    if (!loadedConfig) {
      throw new Error('Could not load APP_CONFIG from /config.js');
    }

    // Merge with defaults to ensure all required properties exist
    const mergedConfig = { ...defaultConfig, ...loadedConfig };

    // Update the global appConfig
    appConfig = mergedConfig;
    return mergedConfig;
  } catch (error) {
    console.error('Failed to load configuration:', error);
    // Update the global appConfig with defaults
    appConfig = defaultConfig;
    return defaultConfig;
  }
};

/**
 * Get the current configuration
 */
export const getConfig = (): AppConfig => {
  return appConfig;
};

/**
 * Set the configuration (used after loading)
 */
export const setConfig = (config: AppConfig): void => {
  appConfig = config;
};

/**
 * Get a specific configuration value
 */
export const getConfigValue = (key: keyof AppConfig): string => {
  // Ensure appConfig is initialized
  if (!appConfig || !appConfig[key]) {
    return defaultConfig[key] || '';
  }
  return appConfig[key] || '';
};

/**
 * Check if a configuration value is truthy
 */
export const isConfigEnabled = (key: keyof AppConfig): boolean => {
  // Ensure appConfig is initialized
  const value = appConfig?.[key] || defaultConfig[key];
  return value === 'true';
};
